import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';

import { ratingTypes } from 'shared/constants/ratingTypes';
import { fetchESGAdditionalDocuments, fetchESGDocuments } from 'services';
import { Documents, Loader } from 'ui';
import { esgAvailable } from 'services/esg';

const DocumentsModal = ({
  defaultExpandDocuments,
  entityId,
  // eslint-disable-next-line no-unused-vars
  companyModule,
  company,
  initialMethodologies,
  initialDocuments,
}) => {
  const { t } = useTranslation();
  const checkExpanded = value => {
    if (defaultExpandDocuments === 'all') return true;

    return value === defaultExpandDocuments;
  };
  const [documents, setDocuments] = useState(initialDocuments);
  const [isDocumentsPending, setIsDocumentsPending] = useState(false);
  const [methodologies, setMethodologies] = useState(initialMethodologies);
  const [isMethodologiesPending, setIsMethodologiesPending] = useState(false);
  const [additionalDocuments, setAdditionalDocuments] = useState([]);
  const [isAdditionalDocumentsPending, setIsAdditionalDocumentsPending] = useState(false);

  const getESGDocuments = async () => {
    setIsDocumentsPending(true);
    const data = await esgAvailable(entityId);
    const reports = [];
    data.availableYears.map(update => {
      const name = t('{{company}} - ESG impact review - {{year}} report', {
        company,
        year: update.year,
      });
      reports.push({
        name,
        title: name,
        path: `/esg/api/generate-report/${entityId}?year=${update.year}&latestUpdate=${update.latestUpdate}`,
        publicationDate: update ? update.latestUpdate : null,
        options: {},
        retries: 14,
      });
      return reports;
    });
    setDocuments(reports);
    setIsDocumentsPending(false);
  };

  const getESGMethodologies = () => {
    setIsMethodologiesPending(true);
    fetchESGDocuments().then(({ data }) => {
      setMethodologies(data || []);
      setIsMethodologiesPending(false);
    });
  };

  const getESGAdditionalDocuments = () => {
    setIsAdditionalDocumentsPending(true);
    fetchESGAdditionalDocuments().then(({ data }) => {
      setAdditionalDocuments(data);
      setIsAdditionalDocumentsPending(false);
    });
  };
  const isEmpty = !documents.length && !methodologies.length && !additionalDocuments.length;

  useEffect(() => {
    switch (companyModule) {
      case ratingTypes.ESG_IMPACT:
        getESGDocuments();
        getESGMethodologies();
        getESGAdditionalDocuments();
        break;
      default:
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <DialogContent>
        {isDocumentsPending ? (
          <Loader center />
        ) : (
          <Documents
            expanded={checkExpanded('reports')}
            title={t('Reports')}
            documents={documents}
          />
        )}
        {isMethodologiesPending ? (
          <Loader center />
        ) : (
          <Documents
            expanded={checkExpanded('methodologies')}
            title={t('Methodologies')}
            documents={methodologies}
          />
        )}
        {isAdditionalDocumentsPending ? (
          <Loader center />
        ) : (
          <Documents
            expanded={checkExpanded('additionalInformation')}
            title={t('Additional information')}
            documents={additionalDocuments}
          />
        )}
        {isEmpty ? <Typography variant="caption">{t('No documents to display')}</Typography> : null}
      </DialogContent>
    </>
  );
};

DocumentsModal.defaultProps = {
  defaultExpandDocuments: 'all',
  initialMethodologies: [],
  initialDocuments: [],
};

const documentProps = PropTypes.arrayOf(
  PropTypes.shape({
    level: PropTypes.string,
    path: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    publicationDate: PropTypes.string,
    title: PropTypes.string.isRequired,
    options: PropTypes.object,
  }).isRequired,
);

DocumentsModal.propTypes = {
  defaultExpandDocuments: PropTypes.oneOf([
    'all',
    'reports',
    'methodologies',
    'additionalInformation',
  ]),
  entityId: PropTypes.string.isRequired,
  companyModule: PropTypes.string.isRequired,
  company: PropTypes.string.isRequired,
  initialMethodologies: documentProps,
  initialDocuments: documentProps,
};

export default DocumentsModal;
